import React, { FC, memo } from 'react';
import styled from '@emotion/styled';
import { productImageUrl } from '~Utils/images';

/**
 * Every item photo in EPOS is a 3:2 crop - the CDN is asked for that ratio,
 * so nothing gets re-cropped client side. Only the size varies per place.
 */
const Frame = styled.div<{ width: string; radius: number; grayscale: boolean }>`
  position: relative;
  flex: 0 0 auto;
  width: ${({ width }) => width};
  aspect-ratio: 3 / 2;
  border-radius: ${({ radius }) => radius}px;
  overflow: hidden;
  background-color: var(--eposCategoryBackground);
  filter: ${({ grayscale }) => (grayscale ? 'grayscale(1)' : 'none')};
`;

const Img = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

interface Props {
  imageUrl: string;
  /** Pixel width to request from the CDN - about the rendered width, doubled for retina. */
  cdnWidth: number;
  /** CSS width of the frame. Height follows from the 3:2 ratio. */
  width?: string;
  borderRadius?: number;
  /** Greys the photo out, e.g. when the item is out of stock. */
  grayscale?: boolean;
  className?: string;
  /** Overlays, e.g. an "out of stock" badge - positioned against the frame. */
  children?: React.ReactNode;
}

const ItemImage: FC<Props> = ({
  imageUrl,
  cdnWidth,
  width = '100%',
  borderRadius = 16,
  grayscale = false,
  className,
  children,
}) => (
  <Frame width={width} radius={borderRadius} grayscale={grayscale} className={className}>
    {!!imageUrl && <Img src={productImageUrl(imageUrl, cdnWidth)} alt="" />}
    {children}
  </Frame>
);

export default memo(ItemImage);
